import { useCallback, useEffect, useState } from 'react';
import type { Settings } from '@core/settings/schema.ts';
import type { SubtitleStore, SubtitleSlot } from '../../state/subtitleStore.ts';
import type { VideoController } from '@ports/VideoController.ts';
import { getDecks, type Deck } from '../../adapters/cards/cardsApi.ts';
import { isPracticeLearningSlot } from './learningHoverPause.ts';
import { installLearningTokenInteraction } from './installLearningTokenInteraction.ts';

export type LearningPopupState = {
  selectedText: string;
  slot: SubtitleSlot;
  clientX: number;
  clientY: number;
};

export type UseLearningOptions = {
  settings: Settings;
  store: SubtitleStore;
  video: VideoController | null;
  unlock: boolean;
  userId: string;
  userToken: string;
  getDocument?: () => Document;
};

export function useLearning({
  settings,
  store,
  video,
  unlock,
  userId,
  userToken,
  getDocument,
}: UseLearningOptions) {
  const [popup, setPopup] = useState<LearningPopupState | null>(null);
  const [decks, setDecks] = useState<Deck[]>([]);
  const [selectedDeckId, setSelectedDeckId] = useState('uncategorized');

  const learningActive = settings.practiceMode === 'learning' && settings.learningMode;

  useEffect(() => {
    if (!learningActive || !unlock || !userId || !userToken) {
      setDecks([]);
      return;
    }
    void getDecks(userId, userToken)
      .then((items) => setDecks(items))
      .catch((err) => console.log(err));
  }, [learningActive, unlock, userId, userToken]);

  const openPopup = useCallback((state: LearningPopupState) => {
    setPopup(state);
  }, []);

  const closePopup = useCallback(() => {
    setPopup(null);
  }, []);

  // Document-delegated token gesture (extension learning.js parity).
  useEffect(() => {
    if (!learningActive) return undefined;
    return installLearningTokenInteraction({
      getSettings: () => ({
        learningMode: settings.learningMode,
        dictationSubtitle: settings.dictationSubtitle,
      }),
      onSelect: openPopup,
      getDocument,
    });
  }, [
    learningActive,
    settings.learningMode,
    settings.dictationSubtitle,
    openPopup,
    getDocument,
  ]);

  const isPracticeSlot = useCallback(
    (slot: SubtitleSlot) => isPracticeLearningSlot(settings, slot),
    [settings],
  );

  return {
    learningActive,
    popup,
    closePopup,
    decks,
    selectedDeckId,
    setSelectedDeckId,
    isPracticeSlot,
    video,
    unlock,
    userId,
    userToken,
    store,
    settings,
  };
}

export type UseLearningResult = ReturnType<typeof useLearning>;
